import { useEffect, useState } from "react";
import api, { apiError, brl, rows } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Ticket, Plus, Pencil } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Button, Checkbox, DataTable, Field, Input, PageHeader, Select } from "@/components/ui-kit";

/** Cupom novo: sem nenhuma regra: é o percentual simples de sempre. */
const VAZIO = {
  code: "", discount_type: "percent", percent: 10, amount: "",
  description: "", valid_from: "", valid_until: "",
  max_uses: "", max_uses_per_customer: "", min_order_total: "",
  scope_categories: [],
};

/** `""` do formulário vira `null` no banco — "sem regra", e não zero. */
const nulo = (v) => (v === "" || v === null || v === undefined ? null : v);
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/** ISO do backend → `value` de <input type="date">. */
const paraData = (iso) => (iso ? String(iso).slice(0, 10) : "");

const rotuloDesconto = (c) => (c.discount_type === "amount" ? brl(c.amount) : `${c.percent}%`);

/**
 * Como o cupom aparece na coluna de validade — o que o lojista precisa saber de
 * relance é se ele ainda está de pé, não as duas datas.
 */
function validade(c) {
  const hoje = new Date().toISOString().slice(0, 10);
  const de = paraData(c.valid_from);
  const ate = paraData(c.valid_until);
  const br = (d) => d.split("-").reverse().join("/");

  if (!de && !ate) return <span className="text-slate-400">sem prazo</span>;
  if (ate && ate < hoje) return <span className="text-red-500">venceu {br(ate)}</span>;
  if (de && de > hoje) return <span className="text-amber-600">a partir de {br(de)}</span>;
  return <span className="text-slate-600">{ate ? `até ${br(ate)}` : `desde ${br(de)}`}</span>;
}

/** Consumo do teto. Sem teto, só o número de usos — não há barra a desenhar. */
function usos(c) {
  const feitos = c.redemptions_count ?? 0;
  if (c.max_uses == null) return <span className="text-slate-500">{feitos}</span>;
  const esgotado = feitos >= c.max_uses;
  return (
    <span className={esgotado ? "text-red-500 font-semibold" : "text-slate-600"}>
      {feitos}/{c.max_uses}
    </span>
  );
}

export default function Coupons() {
  const [editando, setEditando] = useState(null);   // null = modal fechado
  const [form, setForm] = useState(VAZIO);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [categorias, setCategorias] = useState([]);

  const {
    items: coupons, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/coupons");

  // As categorias alimentam o escopo. Falha em silêncio: sem elas o cupom ainda
  // é criável, só não dá para restringir — e o resto da tela segue de pé.
  useEffect(() => {
    api.get("/categories").then((r) => setCategorias(rows(r))).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const abrirNovo = () => { setForm(VAZIO); setEditando("novo"); };

  const abrirEdicao = (c) => {
    setForm({
      code: c.code,
      discount_type: c.discount_type || "percent",
      percent: c.percent ?? "",
      amount: c.amount ?? "",
      description: c.description || "",
      valid_from: paraData(c.valid_from),
      valid_until: paraData(c.valid_until),
      max_uses: c.max_uses ?? "",
      max_uses_per_customer: c.max_uses_per_customer ?? "",
      min_order_total: c.min_order_total ?? "",
      scope_categories: c.scope_categories || [],
    });
    setEditando(c);
  };

  const alternarCategoria = (slug) => setForm((f) => ({
    ...f,
    scope_categories: f.scope_categories.includes(slug)
      ? f.scope_categories.filter((s) => s !== slug)
      : [...f.scope_categories, slug],
  }));

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Informe o código do cupom"); return; }

    const fixo = form.discount_type === "amount";
    if (fixo && !form.amount) { toast.error("Informe o valor em reais do desconto"); return; }
    if (!fixo && !form.percent) { toast.error("Informe o percentual do desconto"); return; }
    if (form.valid_from && form.valid_until && form.valid_until < form.valid_from) {
      toast.error("A data final não pode ser anterior à inicial"); return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      // Só o campo do tipo escolhido vai preenchido: mandar os dois deixaria
      // "10%" pendurado num cupom de R$ 50 e a tela mostraria os dois rótulos.
      percent: fixo ? null : num(form.percent),
      amount: fixo ? num(form.amount) : null,
      description: nulo(form.description.trim()),
      valid_from: nulo(form.valid_from),
      valid_until: nulo(form.valid_until),
      max_uses: num(form.max_uses),
      max_uses_per_customer: num(form.max_uses_per_customer),
      min_order_total: num(form.min_order_total),
      scope_categories: form.scope_categories.length ? form.scope_categories : null,
    };

    setBusy(true);
    try {
      if (editando === "novo") {
        await api.post("/admin/coupons", payload);
        toast.success("Cupom criado");
      } else {
        await api.patch(`/admin/coupons/${editando.id}`, payload);
        toast.success("Cupom atualizado");
      }
      setEditando(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    setTogglingId(c.id);
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Cupom desativado" : "Cupom ativado"); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setTogglingId(null); }
  };

  const remove = async (c) => { await api.delete(`/admin/coupons/${c.id}`); toast.success("Cupom removido"); load(); };

  const fixo = form.discount_type === "amount";

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-coupons-page">
      <PageHeader
        eyebrow="Financeiro"
        title="Cupons & Promoções"
        icon={Ticket}
        description="Desconto aplicado no checkout da loja. Prazo e teto de uso fazem a campanha terminar sozinha — sem depender de alguém lembrar de desativar."
        actions={<Button icon={Plus} onClick={abrirNovo} data-testid="coupon-new-btn">Novo cupom</Button>}
      />

      <DataTable
        caption="Cupons de desconto"
        rows={coupons}
        rowKey={(c) => c.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: Ticket,
          title: "Nenhum cupom cadastrado",
          desc: "Crie o primeiro — ele passa a valer no checkout na hora.",
          action: <Button icon={Plus} onClick={abrirNovo}>Criar cupom</Button>,
          testId: "coupons-empty",
        }}
        columns={[
          { key: "codigo", header: "Código", mono: true, className: "font-semibold text-slate-900", cell: (c) => c.code },
          { key: "desconto", header: "Desconto", align: "right", mono: true, cell: rotuloDesconto },
          { key: "validade", header: "Validade", cell: validade },
          { key: "usos", header: "Usos", align: "right", mono: true, cell: usos },
          {
            key: "regras", header: "Regras", hideOnCard: false,
            cell: (c) => {
              const selos = [];
              if (c.min_order_total) selos.push(`mín. ${brl(c.min_order_total)}`);
              if (c.max_uses_per_customer) selos.push(`${c.max_uses_per_customer}× por cliente`);
              if (c.scope_categories?.length) selos.push(`${c.scope_categories.length} categoria(s)`);
              return selos.length
                ? <span className="text-xs text-slate-500">{selos.join(" · ")}</span>
                : <span className="text-xs text-slate-300">—</span>;
            },
          },
          {
            key: "status", header: "Status", align: "center",
            // O selo é o próprio interruptor: `aria-pressed` diz o estado atual e
            // o rótulo diz o que o clique faz.
            cell: (c) => (
              <button
                type="button"
                onClick={() => toggle(c)}
                disabled={togglingId === c.id}
                aria-pressed={!!c.active}
                aria-label={c.active ? `Desativar cupom ${c.code}` : `Ativar cupom ${c.code}`}
                data-testid="coupon-toggle-btn"
                // `h-7` (28px) parecia selo, mas é o interruptor que liga e
                // desliga o cupom. No celular, com `md:h-7`, volta a ser um
                // alvo de toque; no desktop a densidade da tabela não muda.
                className={`text-xs font-semibold px-3 h-9 md:px-2.5 md:h-7 rounded-full disabled:opacity-50 transition-colors ${
                  c.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              >
                {c.active ? "Ativo" : "Inativo"}
              </button>
            ),
          },
          {
            key: "acoes", header: "Ações", align: "center",
            cell: (c) => (
              <div className="inline-flex items-center gap-1">
                <Button variant="ghost" size="iconSm" onClick={() => abrirEdicao(c)}
                  data-testid="coupon-edit-btn" aria-label={`Editar cupom ${c.code}`}><Pencil size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => setRemoving(c)}
                  data-testid="coupon-delete-btn" aria-label={`Excluir cupom ${c.code}`}
                  className="hover:text-red-500"><Trash2 size={15} /></Button>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="cupons" />

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title={editando === "novo" ? "Novo cupom" : `Editar ${editando?.code || ""}`}
        maxWidth="max-w-2xl"
        testId="coupon-form-modal"
      >
        <form onSubmit={salvar} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Código" className="sm:col-span-1">
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="EX: BEMVINDO10" data-testid="coupon-code-input" className="uppercase"
                // Trocar o código de um cupom que já circulou quebraria quem o
                // tem anotado. Renomear é criar outro.
                disabled={editando !== "novo"} />
            </Field>
            <Field label="Tipo de desconto">
              <Select value={form.discount_type} onChange={set("discount_type")} data-testid="coupon-type-select">
                <option value="percent">Percentual (%)</option>
                <option value="amount">Valor fixo (R$)</option>
              </Select>
            </Field>
            <Field label={fixo ? "Valor (R$)" : "Percentual (%)"}>
              {fixo
                ? <Input type="number" min="0.01" step="0.01" value={form.amount} onChange={set("amount")} data-testid="coupon-amount-input" mono />
                : <Input type="number" min="1" max="100" value={form.percent} onChange={set("percent")} data-testid="coupon-percent-input" mono />}
            </Field>
          </div>

          <fieldset className="border-t border-slate-200 pt-4">
            <legend className="sr-only">Prazo de validade</legend>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-2">Prazo</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Começa em" hint="Em branco: vale desde já.">
                <Input type="date" value={form.valid_from} onChange={set("valid_from")} data-testid="coupon-from-input" />
              </Field>
              <Field label="Vence em" hint="Em branco: não expira.">
                <Input type="date" value={form.valid_until} onChange={set("valid_until")} data-testid="coupon-until-input" />
              </Field>
            </div>
          </fieldset>

          <fieldset className="border-t border-slate-200 pt-4">
            <legend className="sr-only">Limites de uso</legend>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-2">Limites</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Usos no total" hint="Ex.: 50 primeiros.">
                <Input type="number" min="1" value={form.max_uses} onChange={set("max_uses")} data-testid="coupon-maxuses-input" mono placeholder="ilimitado" />
              </Field>
              <Field label="Usos por cliente" hint="Ex.: 1 por pessoa.">
                <Input type="number" min="1" value={form.max_uses_per_customer} onChange={set("max_uses_per_customer")} data-testid="coupon-perclient-input" mono placeholder="ilimitado" />
              </Field>
              <Field label="Pedido mínimo (R$)" hint="Abaixo disso não vale.">
                <Input type="number" min="0" step="0.01" value={form.min_order_total} onChange={set("min_order_total")} data-testid="coupon-mintotal-input" mono placeholder="sem mínimo" />
              </Field>
            </div>
          </fieldset>

          <fieldset className="border-t border-slate-200 pt-4">
            <legend className="sr-only">Categorias alcançadas</legend>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1">Vale para</p>
            <p className="text-xs text-slate-500 mb-3">
              Nenhuma marcada: o cupom desconta o pedido inteiro. Marcando, o desconto
              incide <b>só sobre os itens dessas categorias</b>.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2 max-h-40 overflow-y-auto scrollbar-thin">
              {categorias.map((c) => (
                <Checkbox
                  key={c.slug}
                  checked={form.scope_categories.includes(c.slug)}
                  onChange={() => alternarCategoria(c.slug)}
                  data-testid="coupon-scope-category"
                  label={c.name}
                />
              ))}
            </div>
          </fieldset>

          <Field label="Explicação para o cliente" hint="Aparece quando o cupom é recusado — evita a ligação para o balcão.">
            <Input value={form.description} onChange={set("description")} maxLength={200}
              placeholder="Ex.: Válido só para a linha ortopédica." data-testid="coupon-description-input" />
          </Field>

          <div className="flex gap-3 justify-end border-t border-slate-200 pt-4">
            <Button type="button" variant="ghost" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button type="submit" loading={busy} data-testid="coupon-save-btn">
              {editando === "novo" ? "Criar cupom" : "Salvar"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title="Excluir cupom"
        message={removing ? `Excluir o cupom ${removing.code} (${rotuloDesconto(removing)})? Quem tentar usá-lo receberá “Cupom inválido”.` : ""}
        confirmLabel="Excluir"
      />
    </div>
  );
}
